import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { ContactOptions } from '@propertypal/shared/src/types/contact';
import platform from '@propertypal/shared/src/utils/platform';
import contactSchema from '@propertypal/shared/src/validation/schemas/contactSchema';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CSSProperties } from 'styled-components';
import Button from '../buttons/Button';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import SelectInput from '../inputs/SelectInput';
import TextArea from '../inputs/TextArea';
import TextInput from '../inputs/TextInput';
import { Box } from '../layout';
import Recaptcha, { getCaptchaKey } from '../security/Recaptcha';
import { Bold, SubHeading, Text } from '../typography';
import ContactAgentExample from './ContactAgentExample';
import {
  Container,
  FlexColumn,
  FormContainer,
  FormWrapper,
  InputWrapper,
  SelectWrapper,
  StyledLink,
} from './ContactFormWrapper.style';

interface Props {
  options: ContactOptions;
  sent: boolean;
  setSent: React.Dispatch<React.SetStateAction<boolean>>;
  maxWidth?: number;
  mw?: number;
  flexDirection?: CSSProperties['flexDirection'];
}

const ContactFormWrapper: React.FC<Props> = ({ sent, setSent, options, maxWidth, flexDirection, mw }) => {
  const [sending, setSending] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onSubmit = async (values: any) => {
    setSending(true);
    const endpoint = values.who ? 'public-enquire' : 'agent-enquire';

    try {
      const recaptcha = await getCaptchaKey('enquire');
      const body = {
        name: values.name,
        email: values.email,
        telephone: values.number,
        comments: values.problem,
        recaptcha,
        // as the query select is hidden when selecting `other` & `press` in `Who are you?`
        // this would error as `query` would be an empty string
        // if `press` then sets value as `Press Agency`
        // otherwise default reason to `other` as a fallback
        reason: values.who === 'press' ? 'Press Agency' : values.query || 'other',
      };

      await axios(
        {
          method: 'POST',
          url: `${API_URL}/${endpoint}`,
          data: body,
        },
        dispatch,
      );

      setSent(true);
    } catch {
      openToast({
        title: 'Error',
        message: "Something went wrong. We couldn't send your enquiry. Please try again!",
        type: 'error',
      });
    }

    setSending(false);
  };

  const formik = useFormik({
    initialValues: {
      who: '',
      query: '',
      name: '',
      number: '',
      email: '',
      problem: '',
    },
    validationSchema: contactSchema,
    onSubmit: (values) => {
      if (!sending) onSubmit(values);
    },
  });

  const displayedEmail = useMemo(() => {
    if (formik.values.who === 'press') return platform.pressEmail;
    if (formik.values.who === 'public' || formik.values.who === 'other') return platform.helpEmail;
    return platform.salesEmail;
  }, [formik.values.who]);

  const determineText = () => {
    switch (formik.values.query) {
      case 'enquireProperty':
        return '';
      case 'enquireMortgage':
        return (
          <Text maxWidth={maxWidth} autoMargin={!!maxWidth}>
            If you wish to enquire about a mortgage, fill in the form below to contact us.
          </Text>
        );
      case 'reportProblem':
        return (
          <Box maxWidth={maxWidth} autoMargin={!!maxWidth}>
            <Text>
              If there is a problem with your own property listing, we recommend contacting your estate agent directly.
            </Text>
            <br />
            <Text>
              <Bold>For all other issues, fill in the form below to contact {platform.title}.com.</Bold>
            </Text>
          </Box>
        );
      case 'feedback':
        return (
          <Text maxWidth={maxWidth} autoMargin={!!maxWidth}>
            We love feedback, so if you have any ideas, improvements or new features you&apos;d like added, complete the
            form below.
          </Text>
        );
      case 'support':
        return (
          <Text maxWidth={maxWidth} autoMargin={!!maxWidth}>
            The quickest way to get in touch with our technical team is to submit a support ticket via email to{' '}
            <StyledLink href={`mailto:${platform.helpEmail}`}>{platform.helpEmail}</StyledLink> Alternatively, complete
            the form below:
          </Text>
        );
      default:
        return (
          <Box
            maxWidth={maxWidth}
            autoMargin={!!maxWidth}
            mt={formik.values.who && (formik.values.who === 'other' || formik.values.who === 'press') ? 40 : undefined}
          >
            <Text>
              By phone:{' '}
              <StyledLink href={`tel:${formik.values.who === 'press' ? '+442890760066' : platform.phone}`}>
                {formik.values.who === 'press' ? '+44 28 9076 0066' : platform.phone}
              </StyledLink>
            </Text>
            <br />
            <Text>
              By email: <StyledLink href={`mailto:${displayedEmail}`}>{displayedEmail}</StyledLink>{' '}
            </Text>
            <br />
            <Text>
              <Bold>Alternatively, complete the form below:</Bold>
            </Text>
          </Box>
        );
    }
  };

  const determineOptions = () => {
    if (formik.values.who === 'public') return options.publicQuery;
    if (formik.values.who === 'agent') return options.agentQuery;
    if (formik.values.who === 'developer') return options.devQuery;
    return [];
  };

  useEffect(() => {
    if (formik.values.query) {
      formik.setFieldValue('query', '');
    }
  }, [formik.values.who]);

  return (
    <FormWrapper $mw={mw}>
      <Recaptcha />
      {sent ? (
        <Container
          id="contact"
          data-testid="contact-sent"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 46 }} />
          <SubHeading textTransform="uppercase" mt={20} fontSize={28} fontWeight="600">
            Thanks!
          </SubHeading>
          <Text mt={20} align="center" fontSize={18}>
            Your enquiry has been sent, and a member of the team will be in touch shortly
          </Text>
        </Container>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <SelectWrapper $maxWidth={maxWidth} $mt={45}>
            <SelectInput
              label="Who are you?"
              placeholder="--Select an option--"
              ariaLabel="who"
              id="who"
              options={options.who}
              value={formik.values.who}
              onChange={formik.handleChange('who')}
              labelStyle={platform.select(undefined, { textAlign: 'left' })}
              testID="whoSelect"
            />
          </SelectWrapper>

          {formik.values.who && formik.values.who !== 'other' && formik.values.who !== 'press' && (
            <SelectWrapper $maxWidth={maxWidth} $mt={35} $mb={30}>
              <SelectInput
                label="What is your query?"
                placeholder="--Select an option--"
                ariaLabel="who"
                id="who"
                options={determineOptions()}
                value={formik.values.query}
                onChange={formik.handleChange('query')}
                labelStyle={platform.select(undefined, { textAlign: 'left' })}
                testID="querySelect"
              />
            </SelectWrapper>
          )}

          {(formik.values.query ||
            (formik.values.who && (formik.values.who === 'other' || formik.values.who === 'press'))) && (
            <>
              {determineText()}

              {formik.values.query !== 'enquireProperty' ? (
                <FormContainer $maxWidth={maxWidth} $flexDirection={flexDirection}>
                  <FlexColumn>
                    <TextInput
                      placeholder={platform.select('Your name', undefined)}
                      name="name"
                      value={formik.values.name}
                      onValueChange={formik.handleChange('name')}
                      error={formik.touched.name && formik.errors.name}
                      label={platform.select(undefined, 'Your name')}
                      containerStyle={platform.select(undefined, { textAlign: 'left' })}
                      testID="nameInput"
                    />

                    <InputWrapper>
                      <TextInput
                        placeholder={platform.select('Your telephone number', undefined)}
                        name="number"
                        value={formik.values.number}
                        onValueChange={formik.handleChange('number')}
                        error={formik.touched.number && formik.errors.number}
                        label={platform.select(undefined, 'Your telephone number')}
                        containerStyle={platform.select(undefined, { textAlign: 'left' })}
                        testID="numberInput"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <TextInput
                        placeholder={platform.select('Your email address', undefined)}
                        name="email"
                        value={formik.values.email}
                        onValueChange={formik.handleChange('email')}
                        error={formik.touched.email && formik.errors.email}
                        label={platform.select(undefined, 'Your email address')}
                        containerStyle={platform.select(undefined, { textAlign: 'left' })}
                        testID="emailInput"
                      />
                    </InputWrapper>
                  </FlexColumn>
                  <FlexColumn>
                    <TextArea
                      placeholder={platform.select('Comments', undefined)}
                      name="problem"
                      value={formik.values.problem}
                      onValueChange={formik.handleChange('problem')}
                      rows={5}
                      testID="problemInput"
                      label={platform.select(undefined, 'Comments')}
                      containerStyle={platform.select(undefined, { textAlign: 'left' })}
                      error={formik.touched.problem && formik.errors.problem}
                    />

                    <Button type="submit" loading={sending} testID="submitButton">
                      {platform.select('Submit Form', 'Send Message')}
                    </Button>
                  </FlexColumn>
                </FormContainer>
              ) : (
                <ContactAgentExample />
              )}
            </>
          )}
        </form>
      )}
    </FormWrapper>
  );
};

export default ContactFormWrapper;
